import { useQueries } from '@tanstack/react-query';
import { Wallet, AlertCircle, Activity, TrendingUp, TrendingDown } from 'lucide-react';
import { Link } from 'react-router-dom';

import { StatCard } from '@/components/StatCard';
import { ActivePositionsTable } from '@/components/ActivePositionsTable';
import { usePortfolio, useConfig, useLogs, useAllPositionHistory } from '@/hooks/useApi';
import { formatCurrency, formatPercent, formatPnl, formatPrice } from '@/lib/format';
import { api } from '@/services/api';
import type { PositionHistory } from '@/types';

export function Overview() {
	const { data: portfolio, isLoading: portfolioLoading } = usePortfolio();
	const { data: config, isLoading: configLoading } = useConfig();
	const { data: logs, isLoading: logsLoading } = useLogs(50);
	const { data: positionHistoryData, isLoading: historyLoading } = useAllPositionHistory();

	const isLoading = portfolioLoading || configLoading || logsLoading || historyLoading;

	const activePositions = portfolio?.positions?.filter((p) => p.size > 0) || [];
	const totalPnl = activePositions.reduce((sum, p) => sum + p.unrealizedPnl, 0);
	const errorCount = logs?.logs?.filter((l) => l.level === 'ERROR').length || 0;

	// Fetch latest data for all tracked symbols
	const activeSymbols = config?.activeSymbols || [];
	const latestDataQueries = useQueries({
		queries: activeSymbols.map((symbol) => ({
			queryKey: ['latest', symbol],
			queryFn: () => api.getLatest(symbol),
			refetchInterval: 30000,
			staleTime: 25000,
			enabled: !!symbol
		}))
	});

	// Calculate Net PnL per symbol for different time periods (realized PnL - trading fee + insurance/liquidator fees)
	// Includes both closed positions and open positions with realized PnL or fees
	const calculatePnlPerSymbol = () => {
		const history = positionHistoryData?.history || [];
		const now = Date.now();

		const calculateForPeriod = (days: number) => {
			const cutoffTime = now - days * 24 * 60 * 60 * 1000;
			const pnlBySymbol: Record<string, number> = {};

			history.forEach((pos: PositionHistory) => {
				// Include position if:
				// 1. It was closed within the time window, OR
				// 2. It's still open (no closedAt) but has realized PnL or paid fees
				const isClosedInWindow = pos.closedAt && pos.closedAt >= cutoffTime;
				const isOpenWithRealizedPnlOrFees =
					!pos.closedAt && (pos.realizedPnl !== 0 || pos.tradingFee > 0 || pos.fundingFee !== 0);

				if (isClosedInWindow || isOpenWithRealizedPnlOrFees) {
					if (!pnlBySymbol[pos.symbol]) {
						pnlBySymbol[pos.symbol] = 0;
					}
					// Calculate net PnL: realized PnL - trading fee - funding fee + insurance/liquidator fees
					const netPnl =
						pos.realizedPnl -
						pos.tradingFee -
						pos.fundingFee +
						pos.insuranceFee +
						pos.liquidatorFee;
					pnlBySymbol[pos.symbol] += netPnl;
				}
			});

			return pnlBySymbol;
		};

		return {
			pnl24h: calculateForPeriod(1),
			pnl7d: calculateForPeriod(7),
			pnl30d: calculateForPeriod(30)
		};
	};

	const {
		pnl24h: pnl24hBySymbol,
		pnl7d: pnl7dBySymbol,
		pnl30d: pnl30dBySymbol
	} = calculatePnlPerSymbol();

	// Calculate aggregate realized PnL across all symbols
	const totalRealizedPnl24h = Object.values(pnl24hBySymbol).reduce((sum, pnl) => sum + pnl, 0);
	const totalRealizedPnl7d = Object.values(pnl7dBySymbol).reduce((sum, pnl) => sum + pnl, 0);
	const totalRealizedPnl30d = Object.values(pnl30dBySymbol).reduce((sum, pnl) => sum + pnl, 0);

	// Build symbol summary data
	const symbolSummary = activeSymbols.map((symbol, index) => {
		const latestData = latestDataQueries[index]?.data;
		const position = activePositions.find((p) => p.symbol === symbol);

		return {
			symbol: symbol.replace('PERP_', '').replace('_USDC', ''),
			fullSymbol: symbol,
			price: latestData?.indicators?.candle?.close || 0,
			position: position
				? {
						side: position.isLong ? 'LONG' : 'SHORT',
						size: position.size,
						unrealizedPnl: position.unrealizedPnl,
						entryPrice: position.entryPrice
					}
				: null,
			pnl24h: pnl24hBySymbol[symbol] || 0,
			pnl7d: pnl7dBySymbol[symbol] || 0,
			pnl30d: pnl30dBySymbol[symbol] || 0
		};
	});

	// Sort by 24h PnL (descending)
	const sortedSymbols = [...symbolSummary].sort((a, b) => b.pnl24h - a.pnl24h);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-text-muted">Loading...</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-text">Dashboard Overview</h1>
					<p className="text-text-muted mt-1">
						Environment:{' '}
						<span
							className={`font-medium ${
								config?.environment === 'production' ? 'text-success' : 'text-warning'
							}`}
						>
							{config?.environment?.toUpperCase()}
						</span>
					</p>
				</div>
				<div className="text-right">
					<p className="text-xs text-text-muted">Auto-refresh every 30s</p>
				</div>
			</div>

			{/* Stats Grid */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<div className="card">
					<div className="flex items-start justify-between">
						<div className="flex-1">
							<p className="text-sm font-medium text-text-muted">Balance</p>
							<p className="mt-2 text-3xl font-bold text-text">
								{formatCurrency(portfolio?.balance, 2)}
							</p>
							<div className="mt-3">
								<p className="text-xs text-text-muted mb-2">Realized Net PnL</p>
								<div className="grid grid-cols-3 gap-3">
									<div>
										<p className="text-xs text-text-muted">24h</p>
										<div className="flex items-center gap-1">
											{totalRealizedPnl24h !== 0 && (
												<>
													{totalRealizedPnl24h > 0 ? (
														<TrendingUp className="h-3 w-3 text-success" />
													) : (
														<TrendingDown className="h-3 w-3 text-danger" />
													)}
												</>
											)}
											<p
												className={`text-sm font-medium ${
													totalRealizedPnl24h > 0
														? 'text-success'
														: totalRealizedPnl24h < 0
															? 'text-danger'
															: 'text-text-muted'
												}`}
											>
												{formatPnl(totalRealizedPnl24h)}
											</p>
										</div>
									</div>
									<div>
										<p className="text-xs text-text-muted">7d</p>
										<div className="flex items-center gap-1">
											{totalRealizedPnl7d !== 0 && (
												<>
													{totalRealizedPnl7d > 0 ? (
														<TrendingUp className="h-3 w-3 text-success" />
													) : (
														<TrendingDown className="h-3 w-3 text-danger" />
													)}
												</>
											)}
											<p
												className={`text-sm font-medium ${
													totalRealizedPnl7d > 0
														? 'text-success'
														: totalRealizedPnl7d < 0
															? 'text-danger'
															: 'text-text-muted'
												}`}
											>
												{formatPnl(totalRealizedPnl7d)}
											</p>
										</div>
									</div>
									<div>
										<p className="text-xs text-text-muted">30d</p>
										<div className="flex items-center gap-1">
											{totalRealizedPnl30d !== 0 && (
												<>
													{totalRealizedPnl30d > 0 ? (
														<TrendingUp className="h-3 w-3 text-success" />
													) : (
														<TrendingDown className="h-3 w-3 text-danger" />
													)}
												</>
											)}
											<p
												className={`text-sm font-medium ${
													totalRealizedPnl30d > 0
														? 'text-success'
														: totalRealizedPnl30d < 0
															? 'text-danger'
															: 'text-text-muted'
												}`}
											>
												{formatPnl(totalRealizedPnl30d)}
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="p-3 bg-primary/10 rounded-lg">
							<Wallet className="h-5 w-5 text-primary" />
						</div>
					</div>
				</div>

				<StatCard
					title="Total Unrealized PnL"
					value={formatCurrency(totalPnl, 2)}
					trend={totalPnl > 0 ? 'up' : totalPnl < 0 ? 'down' : 'neutral'}
					trendValue={formatPercent(totalPnl / (portfolio?.balance || 1))}
					icon={<Activity className="h-5 w-5 text-primary" />}
				/>

				<StatCard
					title="Recent Errors"
					value={errorCount}
					subtitle="Last 50 logs"
					trend={errorCount > 0 ? 'down' : 'neutral'}
					icon={<AlertCircle className="h-5 w-5 text-primary" />}
				/>
			</div>

			{/* Symbol Summary */}
			{sortedSymbols.length > 0 && (
				<div>
					<h2 className="text-lg font-semibold text-text mb-4">Symbol Summary</h2>
					<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
						{sortedSymbols.map((symbolData) => (
							<Link
								key={symbolData.fullSymbol}
								to={`/markets/${encodeURIComponent(symbolData.fullSymbol)}`}
								className={`card p-4 block hover:bg-surface-hover hover:border-primary/50 transition-colors ${
									symbolData.position ? 'ring-1 ring-primary/30 bg-primary/5' : ''
								}`}
							>
								{/* Header */}
								<div className="flex items-center justify-between mb-3">
									<h3 className="font-semibold text-text">{symbolData.symbol}</h3>
									{symbolData.position ? (
										<span
											className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
												symbolData.position.side === 'LONG'
													? 'bg-success/20 text-success border border-success/30'
													: 'bg-danger/20 text-danger border border-danger/30'
											}`}
										>
											{symbolData.position.side}
										</span>
									) : (
										<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface text-text-muted border border-border">
											No Position
										</span>
									)}
								</div>

								{/* Current Price */}
								<div className="mb-3">
									<p className="text-xs text-text-muted">Current Price</p>
									<p className="text-lg font-medium text-text">
										{symbolData.price > 0 ? formatPrice(symbolData.price) : '-'}
									</p>
								</div>

								{/* Realized Net PnL Grid */}
								<div className="mb-3">
									<p className="text-xs text-text-muted mb-2">Realized Net PnL</p>
									<div className="grid grid-cols-3 gap-2">
										<div>
											<p className="text-xs text-text-muted">24h</p>
											<div className="flex items-center gap-1">
												{symbolData.pnl24h !== 0 && (
													<>
														{symbolData.pnl24h > 0 ? (
															<TrendingUp className="h-3 w-3 text-success" />
														) : (
															<TrendingDown className="h-3 w-3 text-danger" />
														)}
													</>
												)}
												<p
													className={`text-sm font-medium ${
														symbolData.pnl24h > 0
															? 'text-success'
															: symbolData.pnl24h < 0
																? 'text-danger'
																: 'text-text-muted'
													}`}
												>
													{formatPnl(symbolData.pnl24h)}
												</p>
											</div>
										</div>
										<div>
											<p className="text-xs text-text-muted">7d</p>
											<div className="flex items-center gap-1">
												{symbolData.pnl7d !== 0 && (
													<>
														{symbolData.pnl7d > 0 ? (
															<TrendingUp className="h-3 w-3 text-success" />
														) : (
															<TrendingDown className="h-3 w-3 text-danger" />
														)}
													</>
												)}
												<p
													className={`text-sm font-medium ${
														symbolData.pnl7d > 0
															? 'text-success'
															: symbolData.pnl7d < 0
																? 'text-danger'
																: 'text-text-muted'
													}`}
												>
													{formatPnl(symbolData.pnl7d)}
												</p>
											</div>
										</div>
										<div>
											<p className="text-xs text-text-muted">30d</p>
											<div className="flex items-center gap-1">
												{symbolData.pnl30d !== 0 && (
													<>
														{symbolData.pnl30d > 0 ? (
															<TrendingUp className="h-3 w-3 text-success" />
														) : (
															<TrendingDown className="h-3 w-3 text-danger" />
														)}
													</>
												)}
												<p
													className={`text-sm font-medium ${
														symbolData.pnl30d > 0
															? 'text-success'
															: symbolData.pnl30d < 0
																? 'text-danger'
																: 'text-text-muted'
													}`}
												>
													{formatPnl(symbolData.pnl30d)}
												</p>
											</div>
										</div>
									</div>
								</div>
							</Link>
						))}
					</div>
				</div>
			)}

			{/* Active Positions Table */}
			{activePositions.length > 0 && (
				<ActivePositionsTable
					positions={activePositions}
					header={<h2 className="text-lg font-semibold text-text mb-4">Active Positions</h2>}
				/>
			)}

			{/* Quick Links */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<a href="/positions" className="card hover:bg-surface-hover transition-colors">
					<h3 className="font-medium text-text">View All Positions →</h3>
					<p className="text-sm text-text-muted mt-1">Detailed position management</p>
				</a>
				<a href="/markets" className="card hover:bg-surface-hover transition-colors">
					<h3 className="font-medium text-text">Market Data →</h3>
					<p className="text-sm text-text-muted mt-1">Technical indicators and analysis</p>
				</a>
				<a href="/logs" className="card hover:bg-surface-hover transition-colors">
					<h3 className="font-medium text-text">View Logs →</h3>
					<p className="text-sm text-text-muted mt-1">System logs and errors</p>
				</a>
			</div>
		</div>
	);
}
